import { FC, useCallback, useEffect, useState } from 'react';

import {
  AuditLogActorType,
  useAuditLogsLazyQuery,
} from '../../../../generated/graphql';
import {
  BellIcon,
  ChevronDownIcon,
  CodeIcon,
  DocumentDownloadIcon,
  EyeIcon,
  LinkIcon,
  LockClosedIcon,
  LockOpenIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/outline';

import Avatar from '../../Avatar';
import Pin from '../../../svgs/Pin';
import PinFilled from '../../../svgs/PinFilled';
import ModalDialog from '../../ModalDialog';
import Spinner from '../../../svgs/Spinner';
import { getFullDateTimeStamp, getRelativeTimestamp } from '../../../lib/time';
import {
  formatContactShareLabel,
  getAuditActivityTeamMemberDisplay,
  isExternalAuditActivity,
} from '../../../lib/auditActivityDisplay';

const PROGRAM_NOTE_ENTITY_TYPE = 'app.api.models.program_note.ProgramNote';
const PAGE_SIZE = 20;

type NoteActivityModalProps = {
  isOpen: boolean;
  setClosed: () => void;
  programNoteId: string;
  noteTitle?: string | null;
};

type ContactShareMetadata = {
  notified_provider_user_names?: string[];
  contact_name?: string | null;
  contact_email?: string | null;
  contact_type?: string | null;
  share_id?: string | null;
  contact_id?: string | null;
};

const getActionLabel = (activityType: string): string => {
  switch (activityType) {
    case 'created':
      return 'Created';
    case 'deleted':
      return 'Deleted';
    case 'locked':
      return 'Locked';
    case 'unlocked':
      return 'Unlocked';
    case 'edited':
      return 'Edited';
    case 'pinned':
      return 'Pinned';
    case 'unpinned':
      return 'Unpinned';
    case 'notified':
      return 'Notified';
    case 'downloaded_pdf':
      return 'Downloaded PDF';
    case 'created_by_api':
      return 'Created via API';
    case 'shared_externally':
      return 'Shared externally';
    case 'external_viewed':
      return 'Viewed';
    case 'external_downloaded_pdf':
      return 'Downloaded PDF';
    case 'share_revoked':
      return 'Share revoked';
    default:
      return activityType;
  }
};

const getActionIcon = (activityType: string) => {
  const iconClass = 'h-3.5 w-3.5 shrink-0';
  switch (activityType) {
    case 'created':
      return <PlusIcon className={iconClass} />;
    case 'deleted':
      return <TrashIcon className={iconClass} />;
    case 'locked':
      return <LockClosedIcon className={iconClass} />;
    case 'unlocked':
      return <LockOpenIcon className={iconClass} />;
    case 'edited':
      return <PencilIcon className={iconClass} />;
    case 'pinned':
      return <PinFilled className={iconClass} />;
    case 'unpinned':
      return <Pin className={iconClass} />;
    case 'notified':
      return <BellIcon className={iconClass} />;
    case 'downloaded_pdf':
    case 'external_downloaded_pdf':
      return <DocumentDownloadIcon className={iconClass} />;
    case 'created_by_api':
      return <CodeIcon className={iconClass} />;
    case 'shared_externally':
      return <LinkIcon className={iconClass} />;
    case 'external_viewed':
      return <EyeIcon className={iconClass} />;
    case 'share_revoked':
      return <XCircleIcon className={iconClass} />;
    default:
      return null;
  }
};

type Activity = {
  id: string;
  activityType: string;
  actorType?: AuditLogActorType | string | null;
  performedByProviderUser?: {
    id: string;
    name?: string | null;
    profileImageMedia?: { url: string } | null;
  } | null;
  performedAt: string;
  extraMetadata?: ContactShareMetadata | null;
};

const getDetailsForActivity = (activity: Activity): string => {
  const metadata = activity.extraMetadata;
  if (
    activity.activityType === 'shared_externally' ||
    activity.activityType === 'share_revoked'
  ) {
    return formatContactShareLabel(metadata);
  }
  if (activity.activityType === 'notified') {
    const notifiedNames = metadata?.notified_provider_user_names;
    if (notifiedNames?.length) {
      return notifiedNames.join(', ');
    }
  }
  if (activity.activityType === 'edited') {
    return 'Content updated';
  }
  return '—';
};

const NoteActivityModal: FC<NoteActivityModalProps> = ({
  isOpen,
  setClosed,
  programNoteId,
  noteTitle,
}) => {
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [allActivities, setAllActivities] = useState<Activity[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [fetchAuditLogs, { loading }] = useAuditLogsLazyQuery();

  const loadPage = useCallback(
    async (pageOffset: number, append: boolean) => {
      setLoadError(null);
      const result = await fetchAuditLogs({
        variables: {
          entityType: PROGRAM_NOTE_ENTITY_TYPE,
          entityId: programNoteId,
          limit: PAGE_SIZE,
          offset: pageOffset,
        },
      });

      if (result.error) {
        setLoadError('Unable to load activity. Please try again.');
        setHasMore(false);
        return 0;
      }

      const activities = (result.data?.auditLogs ?? []) as Activity[];
      setAllActivities((prev) =>
        append ? [...prev, ...activities] : activities,
      );
      setOffset(pageOffset + activities.length);
      setHasMore(activities.length >= PAGE_SIZE);
      return activities.length;
    },
    [fetchAuditLogs, programNoteId],
  );

  useEffect(() => {
    if (isOpen && programNoteId) {
      setOffset(0);
      setAllActivities([]);
      setHasMore(true);
      setLoadError(null);
      loadPage(0, false);
    }
  }, [isOpen, programNoteId, loadPage]);

  const handleLoadMore = useCallback(() => {
    loadPage(offset, true);
  }, [loadPage, offset]);

  return (
    <ModalDialog
      title={noteTitle ? `Latest activity for ${noteTitle}` : 'Latest activity'}
      isOpen={isOpen}
      setClosed={setClosed}
      fetching={false}
      width="medium"
      overflow="auto"
      hideFooter
      bodyClassName="min-h-[200px]"
    >
      <div className="flex flex-col">
        {loading && allActivities.length === 0 ? (
          <div className="flex justify-center py-12">
            <Spinner className="h-8 w-8" />
          </div>
        ) : (
          <>
            {allActivities.length > 0 && (
              <div className="overflow-x-auto rounded-md border border-neutral-75">
                <table className="w-full min-w-[400px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-neutral-75 bg-neutral-25">
                      <th className="px-4 py-3 text-small-caption font-medium text-neutral-125">
                        Who
                      </th>
                      <th className="px-4 py-3 text-small-caption font-medium text-neutral-125">
                        Action
                      </th>
                      <th className="px-4 py-3 text-small-caption font-medium text-neutral-125">
                        Details
                      </th>
                      <th className="px-4 py-3 text-small-caption font-medium text-neutral-125">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {allActivities.map((activity) => {
                      const isExternal = isExternalAuditActivity(activity);
                      const teamMember =
                        getAuditActivityTeamMemberDisplay(activity);
                      const name =
                        activity.activityType === 'created_by_api' &&
                        !activity.performedByProviderUser?.name
                          ? 'API'
                          : teamMember.name;
                      const actionLabel = getActionLabel(activity.activityType);
                      const details = getDetailsForActivity(activity);

                      return (
                        <tr
                          key={activity.id}
                          className={
                            isExternal
                              ? 'border-b border-neutral-50 bg-blue-25 last:border-b-0'
                              : 'border-b border-neutral-50 last:border-b-0'
                          }
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {teamMember.isSystem ? (
                                <div
                                  className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white p-0.5"
                                  title="Homecoming"
                                >
                                  <img
                                    src={teamMember.imageUrl}
                                    alt="Homecoming"
                                    className="h-full w-full object-contain"
                                  />
                                </div>
                              ) : (
                                <Avatar
                                  size="small"
                                  imageUrl={teamMember.imageUrl}
                                  name={name}
                                />
                              )}
                              <div className="min-w-0">
                                <span className="block text-caption text-neutral-150">
                                  {name}
                                </span>
                                {teamMember.subtitle && (
                                  <span
                                    className={
                                      isExternal
                                        ? 'block text-small-caption text-blue-125'
                                        : 'block text-small-caption text-secondary-125'
                                    }
                                  >
                                    {teamMember.subtitle}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={
                                isExternal
                                  ? 'inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2 py-0.5 text-small-label font-medium text-blue-150'
                                  : 'inline-flex items-center gap-1.5 rounded-full bg-neutral-50 px-2 py-0.5 text-small-label font-medium text-neutral-150'
                              }
                            >
                              {getActionIcon(activity.activityType)}
                              {actionLabel}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-small-caption text-neutral-125">
                            {details}
                          </td>
                          <td className="px-4 py-3 text-small-caption text-neutral-125">
                            <span
                              title={
                                activity.performedAt
                                  ? getFullDateTimeStamp(activity.performedAt)
                                  : ''
                              }
                            >
                              {activity.performedAt
                                ? getRelativeTimestamp(activity.performedAt)
                                : '—'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {allActivities.length > 0 && hasMore && (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="flex items-center gap-1 text-caption font-medium text-secondary-100 hover:text-secondary-150 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Spinner className="h-4 w-4" />
                      Loading...
                    </>
                  ) : (
                    <>
                      Load more
                      <ChevronDownIcon className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            )}
            {!loading && loadError && (
              <div className="py-12 text-center text-caption text-red-100">
                {loadError}
              </div>
            )}
            {!loading && !loadError && allActivities.length === 0 && (
              <div className="py-12 text-center text-caption text-neutral-125">
                No activity yet.
              </div>
            )}
          </>
        )}
      </div>
    </ModalDialog>
  );
};

export default NoteActivityModal;
